import wapplrClient from 'wapplr'
import wapplrReact from 'wapplr-react'
import wapplrGraphql from 'wapplr-graphql'
import wapplrAuthentication from 'wapplr-authentication'
import wapplrPostTypes from 'wapplr-posttypes'

import setContents, { contentsMiddleware } from '../common/setContents'

import { getConfig as getCommonConfig } from '../common/config'

import initUser from './postTypes/user'
import initImage from './postTypes/image'
import initVideo from './postTypes/video'
import initDocument from './postTypes/document'
import initReferenceLetter from './postTypes/referenceLetter'
import initProfession from './postTypes/profession'
import initPost from './postTypes/post'
import initPage from './postTypes/page'
import initBanner from './postTypes/banner'
import initEmail from './postTypes/email'
import initJob from './postTypes/job'
import initApply from './postTypes/apply'
import initSubscribe from './postTypes/subscribe'

import initProduct from './postTypes/product'
import initMethod from './postTypes/method'
import initOrder from './postTypes/order'
import initPayment from './postTypes/payment'
import initTransaction from './postTypes/transaction'
import initNotification from './postTypes/notification'
import initReview from './postTypes/review'
import initProfile from './postTypes/profile'
import initHistory from './postTypes/history'

import initTransformRequests from '../common/transformRequests'
import getAnalytics from '../common/components/App/analytics'

import initPwa from './pwa'

export function getConfig(p = {}) {

    const { config = {} } = p

    const clientConfig = config.client || {}
    const commonConfig = getCommonConfig(p).config

    const common = { ...commonConfig.common }
    const globals = { ...commonConfig.globals }

    const client = {
        ...clientConfig,
        disableUseDefaultMiddlewares: true,
        ...!globals.DEV ?
            {
                styles: {
                    disableClearMainStyle: true
                }
            } : {}
    }

    return {
        config: {
            ...config,
            common: common,
            client: client
        }
    }

}

export default async function createClient(p) {

    console.log('[VLM] If you notice an error, please report it here: dev@digby.hu')

    const { config } = getConfig(p)
    const wapp = p.wapp || wapplrClient({ ...p, config })

    initPwa({ wapp })

    wapp.pwaInit()

    wapplrPostTypes({ wapp })
    wapplrAuthentication({ wapp })
    wapplrGraphql({ wapp })
    wapplrReact({ wapp })

    setContents({ wapp })

    initUser({ wapp })
    initImage({ wapp })
    initVideo({ wapp })
    initDocument({ wapp })
    initReferenceLetter({ wapp })
    initProfession({ wapp })
    initPost({ wapp })
    initPage({ wapp })
    initBanner({ wapp })
    initEmail({ wapp })
    initJob({ wapp })
    initApply({ wapp })
    initSubscribe({ wapp })

    initProduct({ wapp })
    initMethod({ wapp })
    initOrder({ wapp })
    initPayment({ wapp })
    initTransaction({ wapp })
    initNotification({ wapp })
    initReview({wapp})
    initProfile({ wapp })
    initHistory({ wapp })

    if (!wapp.requests.requestManager._defaultSendRequest) {

        const analytics = getAnalytics({wapp})

        wapp.requests.requestManager._defaultSendRequest = wapp.requests.requestManager.sendRequest
        wapp.requests.requestManager.sendRequest = async (p)=>{
            const response = await wapp.requests.requestManager._defaultSendRequest(p)
            setTimeout(()=>{
                analytics.response({
                    requestParams: p,
                    response,
                })
            }, 1)
            return response
        }
    }

    wapp.requests.requestManager.fetch = async function(...attributes) {
        let r
        try {
            r = await window.fetch(...attributes)
            if (r?.status === 403) {
                try {
                    const { url, options } = wapp.requests.requestManager.requests.userHb
                    const body = options.getBody()
                    await window.fetch(url, { ...options, body: body })
                } catch (e) {
                    console.log(e)
                }
                r = await window.fetch(...attributes)
            }
        } catch (e) {
            if (!window.navigator.onLine) {
                console.log('[PWA] Lost connection')
                if (wapp.client.offlineHandler) {
                    wapp.client.offlineHandler()
                }
            }
        }
        if (r?.status === 404) {
            if (wapp.client.handleNotFoundResponse) {
                await wapp.client.handleNotFoundResponse({ response: r })
            }
        }
        return r
    }

    initTransformRequests({ wapp })

    return wapp
}

export async function createMiddleware() {
    return [
        async function wapplrComMiddleware(req, res, next) {
            await contentsMiddleware(req, res)
            res.wapplrReactEndType = 'pipeWaitForAll'
            next()
        }
    ]

}

const defaultConfig = {
    config: {
        globals: {
            DEV: (typeof DEV !== 'undefined') ? DEV : undefined,
            WAPP: (typeof WAPP !== 'undefined') ? WAPP : undefined,
            RUN: (typeof RUN !== 'undefined') ? RUN : undefined,
            TYPE: (typeof TYPE !== 'undefined') ? TYPE : undefined,
            ROOT: (typeof ROOT !== 'undefined') ? ROOT : '/',
            NAME: (typeof NAME !== 'undefined') ? NAME : undefined
        }
    }
}

export async function run(p = defaultConfig) {

    if (p?.config?.globals && !p.config.globals.RUN) {
        p.config.globals.RUN = p.config?.globals.NAME || 'vlm-web'
    }

    const { config } = getConfig(p)
    const wapp = await createClient({ ...p, config })
    const globals = wapp.globals
    const { DEV } = globals

    const app = wapp.client.app

    app.use(wapp.client.middlewares.wapp)

    app.use(await createMiddleware({ wapp, ...p }))

    app.use([
        ...Object.keys(wapp.client.middlewares).map(function(key) {
            return (key === 'wapp') ? function next(req, res, next) {
                return next()
            } : wapp.client.middlewares[key]
        })
    ])

    wapp.client.middlewares.tags.handle = function defaultHandle(req, res, next) {
        if (res.wappResponse.sended) {

            function updateTag(tagName, keyName, keyValue, attrName, attrValue) {
                const head = document.head
                const selector = `${tagName}[${keyName}="${keyValue}"]`
                const existing = head.querySelector(selector)

                if (existing && existing.getAttribute(attrName) === attrValue) {
                    return existing;
                }

                const nextNode = document.createElement(tagName);
                nextNode.setAttribute(keyName, keyValue);
                if (typeof attrValue === "string") {
                    nextNode.setAttribute(attrName, attrValue);
                }

                if (existing) {
                    existing.parentNode.replaceChild(nextNode, existing);
                    return nextNode;
                }

                const headChildren = Array.from(head.children);

                let anchorSameTag = null;
                for (let i = headChildren.length - 1; i >= 0; i--) {
                    if (headChildren[i].tagName.toLowerCase() === tagName.toLowerCase()) {
                        anchorSameTag = headChildren[i];
                        break;
                    }
                }
                if (anchorSameTag) {
                    head.insertBefore(nextNode, anchorSameTag.nextSibling);
                    return nextNode;
                }

                const firstScript = head.querySelector("script");
                if (firstScript) {
                    head.insertBefore(nextNode, firstScript);
                    return nextNode;
                }

                head.appendChild(nextNode);
                return nextNode;
            }

            function updateMeta(name, content) {
                return updateTag("meta", "name", name, "content", content);
            }

            function updateOg(property, content) {
                return updateTag("meta", "property", property, "content", content);
            }

            function updateCanonical(href) {
                return updateTag("link", "rel", "canonical", "href", href);
            }

            const config = wapp.client.config;
            const { siteName = "Wapplr" } = config;
            const { content = {}, statusCode } = res.wappResponse;

            let { title = "", description = "", author = "", canonical = "" } = content;

            if (typeof title === "function") { title = title({ wapp, req, res }); }
            title = `${title ? title : (statusCode === 404) ? "Not Found | " + siteName : "Untitled Page | " + siteName}`;

            if (typeof description === "function") { description = description({ wapp, req, res }); }
            description = (description) ? description : (title && title.split) ? title.split(" | ")[0] : title;

            if (typeof author === "function") { author = author({ wapp, req, res }); }
            author = (author || siteName);

            if (typeof canonical === "function") { canonical = canonical({ wapp, req, res }); }
            canonical = (canonical || req.wappRequest.protocol + "://" + req.wappRequest.hostname + req.wappRequest.url)

            const url = req.wappRequest.protocol + "://" + req.wappRequest.hostname + req.wappRequest.url

            document.title = title;
            updateMeta("description", description);
            updateMeta("author", author);

            updateCanonical(canonical);
            updateOg("og:url", url);
            updateOg("og:title", title);
            updateOg("og:description", description);

        }
        next();
    }

    wapp.client.listen()

    if (typeof DEV !== 'undefined' && DEV && module.hot) {
        app.hot = module.hot
        module.hot.accept()
    }

    return wapp
}

if (typeof RUN !== 'undefined' && RUN === 'vlm-web') {
    // noinspection JSIgnoredPromiseFromCall
    run()
}
